import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import * as clipboard from 'clipboard-polyfill';
import { BehaviorSubject, merge, of } from 'rxjs';
import { catchError, finalize, map, mapTo, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { SignatureInfo } from '../shared/signature-info';
import { SignatureRendererService } from './signature-renderer.service';
import { NotificationsService } from '../shared/notifications';

@Component({
    selector: 'app-signature-renderer',
    templateUrl: './signature-renderer.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SignatureRendererComponent {
  private readonly signatureInfoSubject = new BehaviorSubject<SignatureInfo | undefined>(undefined);

  readonly renderedSignature$ = this.signatureInfoSubject.pipe(
    switchMap(x => this.sigRendererSvc.renderSignatureInfo(x).pipe(
      catchError((err) => {
        this.notifySvc.addError('There has been an error.  Please try again and if this persists contact appdev@colliercompanies.com');
        return of('');
      })
    )),
    shareReplay(1)
  );

  /** RenderedSignature with sanitizer applied. */
  readonly renderedSignatureSafe$ = this.renderedSignature$.pipe(
    map(x => this.sanitizer.bypassSecurityTrustHtml(x)),
    shareReplay(1)
  );

  /** When the singuture info is changed have state as loading until rendered signature is complete. */
  readonly state$ = merge(
    this.signatureInfoSubject.pipe(mapTo('loading')),
    this.renderedSignatureSafe$.pipe(mapTo('ready')),
  ).pipe(
    tap(() => this.cd.detectChanges())
  );

  @Input()
  set signatureInfo(value: SignatureInfo | undefined) {
    if (value !== this.signatureInfoSubject.value) {
      this.signatureInfoSubject.next(value);
    }
  }

  constructor(
    private cd: ChangeDetectorRef,
    private notifySvc: NotificationsService,
    private sanitizer: DomSanitizer,
    private sigRendererSvc: SignatureRendererService) { }

  copyToClipboard() {
    this.renderedSignature$.pipe(
      take(1),
      tap(content => console.log('copyToClipboard x', content)),
      map(content => new clipboard.ClipboardItem({ 'text/html': new Blob([content], { type: 'text/html' }) })),
      switchMap(x => clipboard.write([x])),      
      finalize(() => this.notifySvc.addSuccess('Signature copied to clipboard.  Now open outlook signatures and paste it in.'))
    ).subscribe();
  }
}
